import * as React from 'react';
import { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { withTranslation } from 'react-i18next';
import {
  UNAVAILABILITY_CONFIG,
  CALENDAR_NOTE_CONFIG,
  UI_COLORS,
} from '../calendarConstants';
import MobileEventCard from './MobileEventCard';
import NonWorkingHoursOverlay from '../NonWorkingHoursOverlay';

const HOUR_HEIGHT = 70; // Slightly larger for mobile touch targets
const TIME_COLUMN_WIDTH = 50;

/**
 * Calculate overlap groups for events on the same day
 * Returns events with overlapIndex and overlapTotal properties for horizontal positioning
 * Ported from CalendarGrid.js
 */
function calculateEventOverlaps(dayEvents) {
  if (!dayEvents.length) return [];

  // Parse and sort events by start time (use clamped hours for multi-day events)
  const eventsWithTimes = dayEvents.map((event) => {
    let startMinutes, endMinutes;

    if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
      // Multi-day event with pre-calculated clamped hours for this day
      startMinutes = event._clampedStartHour * 60;
      endMinutes = event._clampedEndHour * 60;
    } else {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      startMinutes = start.getHours() * 60 + start.getMinutes();
      endMinutes = end.getHours() * 60 + end.getMinutes();
    }

    return {
      ...event,
      startMinutes,
      endMinutes,
    };
  }).sort((a, b) => a.startMinutes - b.startMinutes);

  // Find overlapping groups using a greedy algorithm
  const columns = []; // Each column tracks its end time

  return eventsWithTimes.map((event) => {
    // Find the first column where this event fits (doesn't overlap)
    let columnIndex = columns.findIndex((colEnd) => event.startMinutes >= colEnd);

    if (columnIndex === -1) {
      // No existing column fits, create new one
      columnIndex = columns.length;
      columns.push(event.endMinutes);
    } else {
      // Update the column's end time
      columns[columnIndex] = event.endMinutes;
    }

    return {
      ...event,
      overlapIndex: columnIndex,
      overlapTotal: columns.length, // Will be updated in second pass
    };
  }).map((event, _, arr) => {
    // Second pass: find max columns for events that overlap with this one
    const overlapping = arr.filter(
      (e) => e.startMinutes < event.endMinutes && e.endMinutes > event.startMinutes
    );
    const maxColumns = Math.max(...overlapping.map((e) => e.overlapIndex + 1));
    return {
      ...event,
      overlapTotal: maxColumns,
    };
  });
}

function MobileDayView({
  t,
  selectedDate,
  events,
  unavailabilities,
  calendarNotes = [],
  workSchedule,
  // Grid rows (whole hours, the last one inclusive) and the shown day's work hours to hatch around
  startHour: START_HOUR = 8,
  endHour: END_HOUR = 16,
  dayWorkHours,
  onTimeSlotTap,
  onEventTap,
  selectedResource,
  selectedResourceType,
  isAllView,
  employees,
  serviceBays = [],
}) {
  const scrollContainerRef = useRef(null);
  const [currentTimePosition, setCurrentTimePosition] = useState(null);

  // Generate hours array
  const hours = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) {
    hours.push(h);
  }

  // Scroll to the current time on mount, and when the grid's hours arrive with the calendar data
  useEffect(() => {
    if (scrollContainerRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      if (currentHour >= START_HOUR && currentHour <= END_HOUR) {
        const scrollPosition = (currentHour - START_HOUR) * HOUR_HEIGHT - 50;
        scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
      }
    }
  }, [START_HOUR, END_HOUR]);

  // Update current time line
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const isToday =
        now.toDateString() === selectedDate.toDateString();

      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        if (currentHour >= START_HOUR && currentHour < END_HOUR) {
          const position =
            (currentHour - START_HOUR) * HOUR_HEIGHT +
            (currentMinute / 60) * HOUR_HEIGHT;
          setCurrentTimePosition(position);
        } else {
          setCurrentTimePosition(null);
        }
      } else {
        setCurrentTimePosition(null);
      }
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [selectedDate, START_HOUR, END_HOUR]);

  // Calculate event position and height (use clamped hours for multi-day events)
  const getEventStyle = (event) => {
    let startHour, endHour;

    if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
      // Multi-day event: use pre-calculated clamped hours for this day
      startHour = event._clampedStartHour;
      endHour = event._clampedEndHour;
    } else {
      const startTime = new Date(event.start_time);
      const endTime = new Date(event.end_time);
      startHour = startTime.getHours() + startTime.getMinutes() / 60;
      endHour = endTime.getHours() + endTime.getMinutes() / 60;
    }

    const top = Math.max(0, (startHour - START_HOUR)) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT;

    // Colours live on the card itself (MobileEventCard), which resolves the organization's
    // state overrides and the colour tag; here only geometry.
    return {
      top,
      height: Math.max(height, 30), // Minimum height for visibility
    };
  };

  // Calculate unavailability position and height
  const getUnavailabilityStyle = (unavail) => {
    const startTime = new Date(unavail.start_time);
    const endTime = new Date(unavail.end_time);

    // Clamp to selected date
    const dayStart = new Date(selectedDate);
    dayStart.setHours(START_HOUR, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(END_HOUR, 0, 0, 0);

    const effectiveStart = startTime < dayStart ? dayStart : startTime;
    const effectiveEnd = endTime > dayEnd ? dayEnd : endTime;

    const startHour = effectiveStart.getHours() + effectiveStart.getMinutes() / 60;
    const endHour = effectiveEnd.getHours() + effectiveEnd.getMinutes() / 60;

    const top = Math.max(0, (startHour - START_HOUR)) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT;

    const config = UNAVAILABILITY_CONFIG[unavail.type] || UNAVAILABILITY_CONFIG.personal;

    return {
      top,
      height: Math.max(height, 20),
      backgroundColor: config.bg,
      borderColor: config.color,
    };
  };

  // Calculate calendar note position and height
  const getCalendarNoteStyle = (note) => {
    const startTime = new Date(note.start_time);
    const endTime = new Date(note.end_time);

    const dayStart = new Date(selectedDate);
    dayStart.setHours(START_HOUR, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(END_HOUR, 0, 0, 0);

    const effectiveStart = startTime < dayStart ? dayStart : startTime;
    const effectiveEnd = endTime > dayEnd ? dayEnd : endTime;

    const startHour = effectiveStart.getHours() + effectiveStart.getMinutes() / 60;
    const endHour = effectiveEnd.getHours() + effectiveEnd.getMinutes() / 60;

    const top = Math.max(0, (startHour - START_HOUR)) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT;

    return {
      top,
      height: Math.max(height, 20),
      backgroundColor: CALENDAR_NOTE_CONFIG.bg,
      borderColor: CALENDAR_NOTE_CONFIG.color,
    };
  };

  const handleTimeSlotClick = (hour) => {
    onTimeSlotTap(hour);
  };

  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        bgcolor: 'background.paper',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          minHeight: totalHeight,
        }}
      >
        {/* Time Column */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          {hours.map((hour) => (
            <Box
              key={hour}
              sx={{
                height: HOUR_HEIGHT,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '11px',
                }}
              >
                {hour.toString().padStart(2, '0')}:00
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Events Area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
          }}
        >
          {/* Hour grid lines and clickable areas */}
          {hours.map((hour) => (
            <Box
              key={hour}
              data-testid={`mobile-day-slot-${hour}`}
              onClick={() => handleTimeSlotClick(hour)}
              sx={{
                height: HOUR_HEIGHT,
                borderBottom: '1px solid',
                borderColor: UI_COLORS.hourLine,
                position: 'relative',
                cursor: 'pointer',
                '&:active': {
                  bgcolor: UI_COLORS.selectionBg,
                },
              }}
            >
              {/* Half-hour line */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '1px',
                  bgcolor: UI_COLORS.halfHourLine,
                }}
              />
            </Box>
          ))}

          {/* Outside the shown day's work hours (the last row runs to END_HOUR + 1) */}
          <NonWorkingHoursOverlay
            hours={dayWorkHours}
            startHour={START_HOUR}
            endHour={END_HOUR + 1}
            hourHeight={HOUR_HEIGHT}
          />

          {/* Current time line */}
          {currentTimePosition !== null && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: currentTimePosition,
                height: '2px',
                bgcolor: UI_COLORS.currentTimeLine,
                zIndex: 10,
                pointerEvents: 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -4,
                  top: -4,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: UI_COLORS.currentTimeLine,
                },
              }}
            />
          )}

          {/* Unavailabilities */}
          {unavailabilities.map((unavail) => {
            const style = getUnavailabilityStyle(unavail);
            const config = UNAVAILABILITY_CONFIG[unavail.type] || UNAVAILABILITY_CONFIG.personal;
            return (
              <Box
                key={unavail.id}
                data-testid={`mobile-unavailability-${unavail.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventTap({ ...unavail, _itemType: 'unavailability' });
                }}
                sx={{
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  top: style.top,
                  height: style.height,
                  bgcolor: style.backgroundColor,
                  border: '1px dashed',
                  borderColor: style.borderColor,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.8,
                  zIndex: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: style.borderColor,
                    fontWeight: 500,
                    fontSize: '11px',
                  }}
                >
                  {t(config.labelKey)}
                </Typography>
              </Box>
            );
          })}

          {/* Calendar Notes */}
          {calendarNotes.map((note) => {
            const style = getCalendarNoteStyle(note);
            return (
              <Box
                key={`note-${note.id}`}
                data-testid={`mobile-calendar-note-${note.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventTap({ ...note, _itemType: 'calendar_note' });
                }}
                sx={{
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  top: style.top,
                  height: style.height,
                  bgcolor: style.backgroundColor,
                  borderLeft: '4px solid',
                  borderLeftColor: style.borderColor,
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  px: 1,
                  opacity: 0.9,
                  zIndex: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: CALENDAR_NOTE_CONFIG.text,
                    fontWeight: 600,
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {note.content}
                </Typography>
                {style.height >= 35 && note.created_by_name && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: CALENDAR_NOTE_CONFIG.text,
                      opacity: 0.7,
                      fontSize: '10px',
                    }}
                  >
                    {note.created_by_name}
                  </Typography>
                )}
              </Box>
            );
          })}

          {/* Events with overlap handling */}
          {(() => {
            const eventsWithOverlaps = calculateEventOverlaps(events);
            return eventsWithOverlaps.map((event) => {
              const style = getEventStyle(event);
              // Calculate overlap-aware positioning
              const overlapLeft = event.overlapTotal > 1
                ? `calc(4px + ${(event.overlapIndex / event.overlapTotal) * 100}%)`
                : '4px';
              const overlapWidth = event.overlapTotal > 1
                ? `calc(${100 / event.overlapTotal}% - 8px)`
                : 'calc(100% - 8px)';
              return (
                <MobileEventCard
                  key={event.id}
                  event={event}
                  style={{
                    ...style,
                    left: overlapLeft,
                    width: overlapWidth,
                  }}
                  onClick={() => onEventTap(event)}
                  isAllView={isAllView}
                  employees={employees}
                  serviceBays={serviceBays}
                  showServiceBay={selectedResourceType !== 'bay'}
                />
              );
            });
          })()}
        </Box>
      </Box>
    </Box>
  );
}

export default withTranslation()(MobileDayView);
